import { createApi } from '@reduxjs/toolkit/query/react';

import {
  FormApproveRejectRequest,
  FormApproveRejectResponse,
  ITRequestFormRequest,
  ITRequestFormResponse,
} from '@Types/api/form';
import {
  ApprovalHistory,
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { approvalsListApi } from '@Store/api/approvalsListApiSlice';

export const itRequisitionApi = createApi({
  tagTypes: ['itRequisition'],
  reducerPath: ApiReducerPath.API_IT_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getITRequests: builder.mutation<
      RequestListSearchResponse<ITRequestFormResponse>,
      RequestListSearchRequest
    >({
      query: ({
        searchValue,
        filters = [],
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'requestId',
        sortOrder = 'desc',
      }) => ({
        url: `${ApiBaseName.ESS}/it-requisition/search`,
        method: 'post',
        body: {
          searchValue,
          searchCriteriaList: [
            {
              searchCriterias: filters,
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    getITRequestForm: builder.query<ITRequestFormResponse, string>({
      query: (id) => ({
        url: `${ApiBaseName.ESS}/it-requisition`,
        method: 'get',
        params: { requestId: id },
      }),
      keepUnusedDataFor: 0,
    }),
    deleteITRequest: builder.mutation<null, string>({
      query: (id) => ({
        url: `${ApiBaseName.ESS}/it-requisition`,
        method: 'delete',
        params: { requestId: id },
      }),
    }),
    cancelITRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/it-requisition/cancel`,
        method: 'post',
        params: { requestId },
      }),
    }),
    submitRequest: builder.mutation<ITRequestFormRequest, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/it-requisition/submit`,
        method: 'post',
        params: { requestId },
      }),
    }),
    postITRequestForm: builder.mutation<
      ITRequestFormResponse,
      ITRequestFormRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/it-requisition`,
        method: 'post',
        body,
      }),
    }),
    validateCugMobile: builder.mutation<null, string>({
      query: (empNumber) => ({
        url: `${ApiBaseName.ESS}/it-requisition/validateCugMobile`,
        method: 'post',
        params: { empNumber },
      }),
    }),
    respondITRequest: builder.mutation<
      FormApproveRejectResponse,
      FormApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/it-requisition/command`,
        method: 'post',
        body,
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(
            approvalsListApi.util.invalidateTags(['taskList', 'widgetTaskList'])
          );
        });
      },
    }),
    getItApprovalHistory: builder.mutation<ApprovalHistory[], string>({
      query: (id) => ({
        url: `${ApiBaseName.ESS}/it-requisition/${id}/history`,
        method: 'get',
      }),
    }),
    getExistingPackage: builder.mutation<
      { packages: string | null },
      string | number
    >({
      query: (requisitionId) => ({
        url: `${ApiBaseName.ESS}/it-requisition/${requisitionId}/existing-packages`,
        method: 'get',
      }),
    }),
  }),
});

export const {
  useGetITRequestsMutation,
  usePostITRequestFormMutation,
  useDeleteITRequestMutation,
  useCancelITRequestMutation,
  useSubmitRequestMutation,
  useGetITRequestFormQuery,
  useValidateCugMobileMutation,
  useRespondITRequestMutation,
  useGetItApprovalHistoryMutation,
  useGetExistingPackageMutation,
} = itRequisitionApi;
